import type { HomePromptDTO, HomePromptOption } from "@volley/data";

import type { ThreadMessage } from "./types";

/**
 * A serializable snapshot of an open Ask Volley chat, persisted when the user
 * taps a recommended workout so the chat can be restored if they back out of the
 * workout detail page. The rendered `thread` (incl. fetched carousel cards) is
 * stored verbatim rather than re-fetched on restore — re-running the recommender
 * would re-invoke Gemini (for free text) and re-write attribution snapshots.
 */
export interface AskVolleyChatSnapshot {
    savedAt: number;
    impressionUlid: string;
    /**
     * The sport selected when the chat was open. Carried so events fired after
     * the round-trip — a restored thread, or a help-article read on another
     * screen — land on the same sport as the ask itself. Optional because
     * snapshots written by an earlier bundle don't have it.
     */
    sportId?: number | null;
    prompt: HomePromptDTO;
    initialOptionValue?: string;
    freeTextSeed?: string;
    thread: ThreadMessage[];
    remainingOptions: HomePromptOption[];
}

/**
 * How long a saved chat stays restorable. Bounds the "back to home much later in
 * the session re-opens a stale chat" case — a chat abandoned beyond this window
 * is treated as gone and the home screen loads a fresh prompt instead.
 */
const TTL_MS = 30 * 60 * 1000;

function key(userId: number | undefined): string {
    return `askVolley.openChat.v1.${userId ?? "anon"}`;
}

/** Persist the open chat for `userId`. Best-effort: storage errors are ignored. */
export function saveChatSession(
    userId: number | undefined,
    snapshot: AskVolleyChatSnapshot,
): void {
    try {
        sessionStorage.setItem(key(userId), JSON.stringify(snapshot));
    } catch {
        // Persisting the chat is a nicety; never let it break navigation.
    }
}

/**
 * The saved chat for `userId`, or null when there is none, it can't be parsed, or
 * it is older than the TTL. Read-only — the caller clears it explicitly once
 * consumed.
 */
export function loadChatSession(
    userId: number | undefined,
): AskVolleyChatSnapshot | null {
    try {
        const raw = sessionStorage.getItem(key(userId));
        if (!raw) return null;
        const snapshot = JSON.parse(raw) as AskVolleyChatSnapshot;
        if (
            typeof snapshot.savedAt !== "number" ||
            Date.now() - snapshot.savedAt > TTL_MS
        ) {
            clearChatSession(userId);
            return null;
        }
        return snapshot;
    } catch {
        return null;
    }
}

/** Remove any saved chat for `userId`. */
export function clearChatSession(userId: number | undefined): void {
    try {
        sessionStorage.removeItem(key(userId));
    } catch {
        // Ignore — nothing actionable if storage is unavailable.
    }
}
